/**
 * TopBar — Notebook top bar shown above every signed-in page.
 * Logo, nav (Home/Canvas/Tasks/Runs/Data), active flows, org, credits, account menu.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { SignOut, Monitor, User, Wallet } from '@phosphor-icons/react';
import { useAuth } from '../../context/useAuth';
import { useBalance } from '../../hooks/useBalance';
import { formatCredits, shouldShowBalance } from '../../utils/credits';
import ActiveFlowsMenu from '../ActiveFlowsMenu';
import { Card } from '../ui';

export const NAV_ITEMS = [
  { label: 'Home', to: '/app' },
  { label: 'Canvas', to: '/canvas' },
  { label: 'Tasks', to: '/tasks' },
  { label: 'Runs', to: '/runs' },
  { label: 'Data', to: '/data' },
];

const FOCUS = 'focus-ring';
const ITEM = `w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-hl-soft ${FOCUS}`;

export default function TopBar() {
  const { user, logout, signOutEverywhere } = useAuth();
  const { balance } = useBalance(shouldShowBalance(user?.tier));
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const go = (to) => { setOpen(false); navigate(to); };
  const handleLogout = async () => { await logout(); navigate('/login'); };
  const handleSignOutEverywhere = async () => {
    setOpen(false);
    await signOutEverywhere();
    navigate('/login');
  };

  const credits = shouldShowBalance(user?.tier) ? formatCredits(balance?.availableCredits) : '';
  const initial = ((user?.full_name || user?.email || '?').trim()[0] || '?').toUpperCase();

  return (
    <header className="h-16 bg-card border-b-1.5 border-ink flex items-center px-8 gap-9 text-ink">
      <Link to="/app" className={`flex items-center gap-2.5 font-display font-extrabold text-2xl tracking-tight rounded-sm ${FOCUS}`}>
        <svg width="20" height="14" viewBox="0 0 20 14" aria-hidden="true">
          <circle cx="7" cy="7" r="5.2" fill="none" stroke="#1B1F3B" strokeWidth="2" />
          <circle cx="13" cy="7" r="5.2" fill="none" stroke="#1B1F3B" strokeWidth="2" />
        </svg>
        flowize
      </Link>

      <nav aria-label="Main" className="flex shrink-0 gap-1.5 font-semibold text-[15px]">
        {NAV_ITEMS.map(({ label, to }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/app'}
            className={({ isActive }) =>
              `px-3.5 py-2 rounded-[10px] ${FOCUS} ${
                isActive ? 'bg-hl-soft text-ink border-b-2 border-ink' : 'text-pencil hover:text-ink'
              }`
            }
          >
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="ml-auto flex items-center gap-4 text-sm">
        <ActiveFlowsMenu />
        {user?.organisation_name && <span className="text-pencil truncate max-w-[16ch]" title={user.organisation_name}>{user.organisation_name}</span>}
        {credits && <b>{credits}</b>}
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            aria-label="Account menu"
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            title={user?.email}
            className={`w-8 h-8 rounded-full bg-ink text-white font-bold flex items-center justify-center ${FOCUS}`}
          >
            {initial}
          </button>
          {open && (
            <Card variant="lifted" className="absolute right-0 mt-2 w-56 py-1 z-50 overflow-hidden">
              <button type="button" className={ITEM} onClick={() => go('/profile')}>
                <User size={16} weight="duotone" /><span>Profile &amp; settings</span>
              </button>
              <button type="button" className={ITEM} onClick={() => go('/billing')}>
                <Wallet size={16} weight="duotone" /><span>Wallet &amp; balance</span>
              </button>
              <div className="border-t border-rule my-1" />
              <button type="button" className={ITEM} onClick={handleLogout}>
                <SignOut size={16} weight="duotone" /><span>Logout</span>
              </button>
              <button type="button" className={ITEM} onClick={handleSignOutEverywhere}>
                <Monitor size={16} weight="duotone" /><span>Sign out everywhere</span>
              </button>
            </Card>
          )}
        </div>
      </div>
    </header>
  );
}
